import { ContactCommonResponse, DependableContactResponse } from "./contact";
import { DeviceRole } from "./role";

export enum DeviceSortableColumns {
  SERIAL_NUMBER = "SERIAL_NUMBER",
  MODEL = "MODEL",
  NAME = "NAME",
  STATE = "STATE",
  CUSTOMER = "CUSTOMER",
  CONNECTED = "CONNECTED",
  SUBSCRIPTION_STATE = "SUBSCRIPTION_STATE",
}
export type DeviceDetails = {
  nickname: string | null;
  warrantyStartDate: string | null;
  warrantyEndDate: string | null;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
  location: AddressInfo;
};

export type Device = {
  id: number;
  activated: string | null;
  connected: boolean;
  model: string;
  serialNumber: string;
  sipSettings: SipSettings;
  simSettings: SimSettings;
  contactInfo: ContactInfo;
  deviceSettings: DeviceSettings;
  deviceDetails: DeviceDetails;
  carers: DeviceRole[];
  registered: boolean;
};

export type DeviceState = {
  faultCode: string;
  currentLocation: DeviceLocation;
  currentLocationTimestamp: string;
  footRestPosition: FootRestPosition;
  seatPosition: SeatPosition;
  hingePosition: HingePosition;
  swivelPosition: SwivelPosition;
  seatbeltStatus: SeatBeltStatus;
  chargerStatus: ChargerStatus;
};

export type NetworkInfo = {
  imsi: string;
  imei: string;
  iccid: string;
  cid: string;
  lac: string;
  tac: string;
  timestamp: string;
};

export type DeviceSettings = {
  id: number;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
  subscriptionState: SubscriptionState;
  deviceUiLanguage: DeviceUILanguage;
  liftGuiTelemetryEnabled: boolean;
  networkStateUpdateInterval: number;
  networkStateTelemetryEnabled: boolean;
  liftEcuUpdateInterval: number;
  batteryVoltageTelemetryEnabled: boolean;
  batteryVoltageReadInterval: number;
  runtimeInfoUpdateInterval: number;
  runtimeInfoTelemetryEnabled: boolean;
  temperatureUpdateInterval: number;
  temperatureTelemetryEnabled: boolean;
};

export type OtaDownloadSettings = {
  url: string;
  token: string;
  controllerId: string;
};

export type LogsDownloadSettings = {
  url: string;
  token: string;
  tokenValidFor: string;
};

export type CommissioningDataDownloadSettings = {
  url: string;
  token: string;
  tokenValidFor: string;
};

export enum DeviceLocation {
  MOVING_UP = "MOVING_UP",
  MOVING_DOWN = "MOVING_DOWN",
  BOTTOM_STOP_POINT = "BOTTOM_STOP_POINT",
  BOTTOM_STOP_AND_PARK_POINT = "BOTTOM_STOP_AND_PARK_POINT",
  MIDDLE_STOP_POINT = "MIDDLE_STOP_POINT",
  MIDDLE_STOP_AND_PARK_POINT = "MIDDLE_STOP_AND_PARK_POINT",
  UPPER_STOP_POINT = "UPPER_STOP_POINT",
  UPPER_STOP_AND_PARK_POINT = "UPPER_STOP_AND_PARK_POINT",
  PARK_POINT = "PARK_POINT",
  STOPPED = "STOPPED",
  UNKNOWN = "UNKNOWN",
}

export enum FootRestPosition {
  IN_TRANSIT = "IN_TRANSIT",
  UNFOLDED = "UNFOLDED",
  FOLDED = "FOLDED",
  UNKNOWN = "UNKNOWN",
}

export enum SeatPosition {
  IN_TRANSIT = "IN_TRANSIT",
  UNFOLDED = "UNFOLDED",
  FOLDED = "FOLDED",
  PARTIALLY_UNFOLDED = "PARTIALLY_UNFOLDED",
  UNKNOWN = "UNKNOWN",
}

export enum HingePosition {
  IN_TRANSIT = "IN_TRANSIT",
  FOLDED = "FOLDED",
  RETRACTED = "RETRACTED",
  UNKNOWN = "UNKNOWN",
}

export enum SwivelPosition {
  IN_TRANSIT = "IN_TRANSIT",
  CENTERED = "CENTERED",
  SWIVELLED_LEFT = "SWIVELLED_LEFT",
  SWIVELLED_RIGHT = "SWIVELLED_RIGHT",
  UNKNOWN = "UNKNOWN",
}

export enum SeatBeltStatus {
  OPEN = "OPEN",
  CLOSED = "CLOSED",
  UNKNOWN = "UNKNOWN",
}

export enum ChargerStatus {
  CHARGING = "CHARGING",
  NOT_CHARGING = "NOT_CHARGING",
  UNKNOWN = "UNKNOWN",
}

export enum SubscriptionState {
  ACTIVE = "ACTIVE",
  SUSPENDED = "SUSPENDED",
  UNREGISTERED = "UNREGISTERED",
}

export enum DeviceUILanguage {
  ENGLISH = "ENGLISH",
  FRENCH = "FRENCH",
  ITALIAN = "ITALIAN",
}

export type SipSettings = {
  registrar: string | null;
  realm: string | null;
  username: string | null;
  password: string | null;
  phoneNumber: string | null;
  audioCodec: string | null;
};

export type SimSettings = {
  username: string | null;
  password: string | null;
  apn: string | null;
};

export type Warranty = {
  warrantyStartDate: string | null;
  warrantyEndDate: string | null;
};

export type CompanyContact = {
  id: number;
  displayName: string | null;
  phoneNr: string;
};

export type ContactInfo = {
  customerId: number;
  speedDialNumber: CompanyContact;
  emergencyNumber: CompanyContact;
  supportNumber: CompanyContact;
  nickname: string;
  dependableContacts: DependableContactResponse[];
  otherContacts: ContactCommonResponse[];
};

export type DeviceInfo = {
  id: number;
  manufacturer: string | null;
  model: string | null;
  swVersion: string | null;
  osName: string | null;
  processorArchitecture: string | null;
  processorManufacturer: string | null;
  totalStorage: number | null;
  totalMemory: number | null;
};

export type DeviceListResponse = {
  id: number;
  serialNumber: string;
  tenantId: number;
  nickname: string | null;
  subscriptionState: SubscriptionState;
};

export type UpdateCarersRequest = {
  carers: CarerRequest[];
};

export type CarerRequest = {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
};

export type DeviceRegistrationItem = {
  serialNumber: string;
  nickname?: string;
};

export type AddressInfo = {
  address: string | null;
  apartmentNr: string | null;
  zipCode: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
};

export type DeviceHistoryData = {
  tenantId: string;
  deviceId: string;
  key: string;
  value: string;
  type: string;
  timestamp: string;
};
